// Config for the nav category landing pages (Speciality Tours, Customized Holidays, etc.)
import { TOURS } from "@/data/tours";

const M = (f) => `/assets/media/${f}`;

const HERO = {
  speciality: M("048-images-unsplash-com-photo-1675185991531-9c2aee461355.jpg"),
  customized: M("038-images-unsplash-com-photo-1602216056096-3b40cc0c9944.jpg"),
  corporate: M("049-images-unsplash-com-photo-1749849934727-bc57c0bd9dd2.jpg"),
  inbound: M("030-images-unsplash-com-photo-1564507592333-c60657eea523.jpg"),
  visa: M("013-images-unsplash-com-photo-1502602898657-3e91760cbb34.jpg"),
};

const SPECIALITY_SET = new Set([
  "Honeymoon Special",
  "Women's Special",
  "Seniors' Special",
  "Pilgrimage",
  "Road Trips",
]);

export const CATEGORIES = {
  "speciality-tours": {
    label: "Speciality Tours",
    tagline: "Tailor-made experiences for every kind of traveller",
    description:
      "Whether it's a dreamy honeymoon, a women-only getaway, a serene senior-citizen journey or a soulful pilgrimage — our speciality tours are thoughtfully designed around who you are and how you love to travel.",
    hero: HERO.speciality,
    items: [
      { icon: "Heart", label: "Honeymoon", desc: "Romantic escapes for two" },
      { icon: "Users", label: "Women Only", desc: "Safe, fun all-women groups" },
      { icon: "Compass", label: "Senior Citizens", desc: "Relaxed pace, extra care" },
      { icon: "Church", label: "Pilgrimage", desc: "Sacred journeys of faith" },
      { icon: "Sparkles", label: "Solo Travel", desc: "Meet like-minded explorers" },
      { icon: "Mountain", label: "Adventure", desc: "For the thrill-seekers" },
    ],
    showTours: true,
    filter: (t) => SPECIALITY_SET.has(t.speciality),
  },
  "customized-holidays": {
    label: "Customized Holidays",
    tagline: "Your holiday, designed exactly your way",
    description:
      "Prefer to travel on your own dates with your own pace? Our travel designers craft fully personalised itineraries — handpicked hotels, private transfers and the experiences you care about most.",
    hero: HERO.customized,
    items: [
      { icon: "Briefcase", label: "Family Fun", desc: "Something for every age" },
      { icon: "Heart", label: "Romantic Holidays", desc: "Just the two of you" },
      { icon: "CalendarCheck", label: "Fixed Date Holiday", desc: "Guaranteed departures" },
      { icon: "Car", label: "Self Drive", desc: "Freedom on the road" },
      { icon: "Ship", label: "Cruise Holidays", desc: "Sail in style" },
      { icon: "Map", label: "Hidden Gems", desc: "Off the beaten path" },
    ],
    showTours: true,
    filter: (t) => t.packageType === "Customized Holidays" || t.speciality === "Family",
  },
  inbound: {
    label: "Inbound India",
    tagline: "Discover the soul of Incredible India",
    description:
      "Curated journeys for international travellers exploring India — from the Golden Triangle and Himalayan odysseys to the backwaters of the South and the spiritual heart of Varanasi, with English-speaking guides throughout.",
    hero: HERO.inbound,
    items: [
      { icon: "Crown", label: "Golden Triangle", desc: "Delhi · Agra · Jaipur" },
      { icon: "Mountain", label: "Himalayan Odyssey", desc: "Kashmir · Ladakh" },
      { icon: "Palmtree", label: "South India", desc: "Kerala & beyond" },
      { icon: "Landmark", label: "Heritage Tours", desc: "Forts & palaces" },
      { icon: "Sparkles", label: "Spiritual India", desc: "Temples & rivers" },
      { icon: "Leaf", label: "Wildlife & Nature", desc: "Tigers & jungles" },
    ],
    showTours: true,
    filter: (t) => t.scope === "india",
  },
  "corporate-travel": {
    label: "Corporate Travel & MICE",
    tagline: "Meetings, incentives, conferences & unforgettable off-sites",
    description:
      "From high-energy team off-sites to global incentive trips and seamless business travel, Royal Tours' dedicated MICE desk handles logistics end-to-end so your team can focus on what matters.",
    hero: HERO.corporate,
    items: [
      { icon: "Briefcase", label: "Corporate Off-sites", desc: "Recharge & align teams" },
      { icon: "Users", label: "Team Building", desc: "Bond beyond the boardroom" },
      { icon: "CalendarCheck", label: "Conferences", desc: "End-to-end event logistics" },
      { icon: "Sparkles", label: "Incentive Trips", desc: "Reward top performers" },
      { icon: "Plane", label: "Business Travel", desc: "Flights, stays, transfers" },
      { icon: "Crown", label: "Executive Retreats", desc: "Premium, private, seamless" },
    ],
    showTours: false,
  },
  "visa-services": {
    label: "Visa Services",
    tagline: "Fast, hassle-free visas for 60+ countries",
    description:
      "Skip the paperwork stress. Our visa experts guide you through documentation, appointments and submissions with the highest approval success — for tourist, business and transit visas worldwide.",
    hero: HERO.visa,
    items: [
      { icon: "Plane", label: "Schengen Visa", desc: "26 European countries" },
      { icon: "Crown", label: "UK Visa", desc: "Standard visitor visa" },
      { icon: "Sparkles", label: "USA Visa", desc: "B1/B2 assistance" },
      { icon: "Compass", label: "Canada Visa", desc: "Visitor & eTA" },
      { icon: "Landmark", label: "Dubai Visa", desc: "48hr express options" },
      { icon: "Map", label: "Japan Visa", desc: "Tourist & transit" },
    ],
    showTours: false,
  },
};

export const CATEGORY_ROUTE = {
  "Speciality Tours": "/category/speciality-tours",
  "Customized Holidays": "/category/customized-holidays",
  "Inbound": "/category/inbound",
  "Corporate Travel": "/category/corporate-travel",
  "Visa Services": "/category/visa-services",
};

// Which tours each menu / tile item shows (any phrase may match, or a custom test)
export const ITEM_QUERIES = {
  Honeymoon: ["honeymoon"],
  "Romantic Holidays": ["honeymoon", "romantic", "couple"],
  "Women Only": ["women"],
  "Senior Citizens": ["senior"],
  "Solo Travel": ["solo"],
  Pilgrimage: ["pilgrimage", "yatra", "darshan", "jyotirlinga", "temple"],
  "Yoga & Wellness": ["yoga", "wellness", "rishikesh"],
  Adventure: ["adventure", "ladakh", "trek", "safari"],
  "Photo Tours": ["photo", "wildlife", "safari"],
  "Family Fun": ["family"],
  Getaways: ["getaway", "short trips"],
  "Fixed Date Holiday": ["fixed date"],
  "Self Drive Holidays": ["self drive", "road trips"],
  "Self Drive": ["self drive", "road trips"],
  "Cruise Holidays": ["cruise"],
  "Hidden Gems": ["hidden", "offbeat", "spiti", "ziro"],
  "Air Inclusive Holidays": (t) => t.inclusions?.flight === true,
  "Golden Triangle": ["golden triangle", "agra jaipur", "delhi agra"],
  "Himalayan Odyssey": ["kashmir", "ladakh", "himachal", "uttarakhand", "sikkim"],
  "South India": ["south india", "kerala", "tamil nadu", "karnataka"],
  "Wildlife & Nature": ["wildlife", "national park", "safari", "corbett", "ranthambore", "kaziranga"],
  "Heritage Tours": ["heritage", "fort", "palace"],
  "Spiritual India": ["varanasi", "ayodhya", "pilgrimage", "temple"],
};

export const getCategory = (key) => CATEGORIES[key];

export const getCategoryTours = (cfg) =>
  cfg.showTours && cfg.filter ? TOURS.filter(cfg.filter) : [];

// Tours for one item inside a category: first within the category, else across all tours.
export const getItemTours = (cfg, item, matchText) => {
  const rule = ITEM_QUERIES[item];
  if (!cfg.showTours || !rule) return [];
  const test = typeof rule === "function" ? rule : (t) => rule.some((q) => matchText(t, q));
  const inCategory = getCategoryTours(cfg).filter(test);
  return inCategory.length ? inCategory : TOURS.filter(test);
};
